import React, { useState } from 'react';
import { Send, CheckCircle, Mail, Phone } from 'lucide-react';
import { leadsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';

const INITIAL_FORM = {
  origin: '',
  destination: '',
  departure_date: '',
  return_date: '',
  flexible_dates: false,
  checked_baggage: false,
  travel_insurance: false,
  passengers: 1,
  email: '',
  whatsapp: ''
};

const TravelFields = ({ formData, onChange }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div>
        <Label htmlFor="origin">Origem</Label>
        <Input id="origin" name="origin" value={formData.origin} onChange={onChange} required placeholder="Ex: São Paulo" data-testid="origin-input" />
      </div>
      <div>
        <Label htmlFor="destination">Destino</Label>
        <Input id="destination" name="destination" value={formData.destination} onChange={onChange} required placeholder="Ex: Lisboa" data-testid="destination-input" />
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div>
        <Label htmlFor="departure_date">Data de Ida</Label>
        <Input id="departure_date" name="departure_date" type="date" value={formData.departure_date} onChange={onChange} required data-testid="departure-date-input" />
      </div>
      <div>
        <Label htmlFor="return_date">Data de Volta</Label>
        <Input id="return_date" name="return_date" type="date" value={formData.return_date} onChange={onChange} data-testid="return-date-input" />
      </div>
    </div>
    <div>
      <Label htmlFor="passengers">Número de Passageiros</Label>
      <Input id="passengers" name="passengers" type="number" min="1" value={formData.passengers} onChange={onChange} required data-testid="passengers-input" />
    </div>
  </>
);

const OptionsFields = ({ formData, setFormData }) => (
  <div className="bg-slate-50 rounded-xl p-4 space-y-3">
    <p className="text-sm font-medium text-slate-700 mb-1">Opções adicionais</p>
    {[
      { id: 'flexible_dates', label: 'Datas flexíveis' },
      { id: 'checked_baggage', label: 'Preciso de bagagem despachada' },
      { id: 'travel_insurance', label: 'Desejo seguro viagem' },
    ].map(({ id, label }) => (
      <div key={id} className="flex items-center space-x-2">
        <Checkbox
          id={id}
          checked={formData[id]}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, [id]: checked }))}
          data-testid={`${id.replace('_', '-')}-checkbox`}
        />
        <Label htmlFor={id} className="cursor-pointer text-sm">{label}</Label>
      </div>
    ))}
  </div>
);

const ContactFields = ({ formData, onChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
    <div>
      <Label htmlFor="email" className="flex items-center gap-1.5">
        <Mail className="w-3.5 h-3.5 text-slate-400" /> Email
      </Label>
      <Input id="email" name="email" type="email" value={formData.email} onChange={onChange} required placeholder="seu@email.com" data-testid="email-input" />
    </div>
    <div>
      <Label htmlFor="whatsapp" className="flex items-center gap-1.5">
        <Phone className="w-3.5 h-3.5 text-slate-400" /> WhatsApp
      </Label>
      <Input id="whatsapp" name="whatsapp" type="tel" value={formData.whatsapp} onChange={onChange} required placeholder="+55 11 99999-9999" data-testid="whatsapp-input" />
    </div>
  </div>
);

const SuccessMessage = ({ onReset }) => (
  <div className="text-center py-8" data-testid="quote-success">
    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <CheckCircle className="w-8 h-8 text-green-600" />
    </div>
    <h3 className="text-xl font-bold text-slate-900 mb-2">Solicitação Enviada!</h3>
    <p className="text-slate-600 text-sm mb-1">
      Recebemos sua cotação e entraremos em contato em breve.
    </p>
    <p className="text-slate-500 text-xs mb-6">
      Você também pode nos contatar pelo WhatsApp para agilizar.
    </p>
    <Button
      onClick={onReset}
      variant="outline"
      className="rounded-full"
      data-testid="new-quote-button"
    >
      Solicitar outra cotação
    </Button>
  </div>
);

export const QuoteForm = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await leadsAPI.create(formData);
      
      const whatsappMessage = encodeURIComponent(
        `Olá! Acabei de enviar uma cotação pelo site.\nOrigem: ${formData.origin}\nDestino: ${formData.destination}\nPassageiros: ${formData.passengers}`
      );
      window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${whatsappMessage}`, '_blank');
      
      if (response.data?.email_sent) {
        toast.success('Cotação enviada! Você receberá uma resposta por email.');
      } else {
        toast.success('Cotação enviada com sucesso!');
      }
      
      setSubmitted(true);
      setFormData(INITIAL_FORM);
    } catch {
      toast.error('Erro ao enviar solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => setSubmitted(false);

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8" data-testid="quote-form">
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Peça sua Cotação</h2>
        <p className="text-sm text-slate-500">
          Preencha o formulário e receba uma proposta personalizada por email e WhatsApp.
        </p>
      </div>

      {submitted ? (
        <SuccessMessage onReset={handleReset} />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <TravelFields formData={formData} onChange={handleChange} />
          <OptionsFields formData={formData} setFormData={setFormData} />
          <ContactFields formData={formData} onChange={handleChange} />

          <Button 
            type="submit" 
            className="w-full bg-[#00A8B5] hover:bg-[#008C99] text-white font-semibold py-6 text-base sm:text-lg rounded-full"
            disabled={loading}
            data-testid="submit-quote-button"
          >
            {loading ? 'Enviando...' : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Encontrar Promoção para Mim
              </>
            )}
          </Button>

          <p className="text-xs text-center text-slate-400">
            Ao enviar, você será redirecionado ao WhatsApp para contato direto.
          </p>
        </form>
      )}
    </div>
  );
};
